import { useEffect, useMemo, useState } from 'react'

import { useQuery } from 'react-query'
import { useSelector } from 'react-redux'

import { type RequestableCreditCardOption } from '@webapp/components/RequestableCreditCardSelect'
import { Currency } from '@webapp/core/entities/external/Currency'
import { makeSelectBaseCurrency } from '@webapp/core/stores/global'
import { type ExpenseFormInitialData } from '@webapp/modules/expense/business-logic/use-cases/GetExpenseRequestFormData'
import { getExpenseRequestFormDataUseCase } from '@webapp/modules/expense/business-logic/use-cases/GetExpenseRequestFormData'
import { type ExpenseReportFormSchema } from '@webapp/modules/expense/components/CreateExpenseReportForm'
import { expenseQueryConfig } from '@webapp/modules/expense/query-configs/expense'

const DEFAULT_FORM_VALUES: ExpenseReportFormSchema = {
  id: null,
  title: '',
  location: null,
  department: null,
  travel: null,
  notes: '',
  createdDate: '',
  nextApproverId: null,
  items: [],
}

function mapFormInitialDataToSchema(
  data: ExpenseFormInitialData
): ExpenseReportFormSchema {
  const { draft, defaultLocation, defaultDepartment, items } = data

  if (!draft) {
    return {
      id: null,
      title: '',
      location: defaultLocation
        ? { id: defaultLocation.id, name: defaultLocation.name }
        : null,
      department: defaultDepartment
        ? { id: defaultDepartment.id, name: defaultDepartment.name }
        : null,
      travel: null,
      notes: '',
      createdDate: '',
      nextApproverId: null,
      items: [],
    }
  }

  return {
    id: draft.id,
    title: draft.title,
    department: defaultDepartment
      ? { id: defaultDepartment.id, name: defaultDepartment.name }
      : null,
    location: defaultLocation
      ? { id: defaultLocation.id, name: defaultLocation.name }
      : null,
    travel: null,
    notes: draft.notes,
    createdDate: draft.createdDate,
    nextApproverId: draft.nextApproverId,
    items: items.map((item) => ({
      id: item.id,
      merchant: item.merchant,
      account: item.account
        ? {
            id: item.account.id,
            accountCode: {
              id: item.account.accountCode.id,
              code: item.account.accountCode.code,
              description: item.account.accountCode.description,
            },
          }
        : null,
      currency: item.currency,
      reimbursable: item.reimbursable,
      expenseType: item.expenseType,
      paymentType: item.paymentType,
      // Map the draft's partial credit card shape to a display-only option for
      // RequestableCreditCardSelect. The Select only uses id/name/number/type
      // for display; currency/vendor are null because the draft API does not
      // provide full credit card details.
      paymentMethod: item.creditCard
        ? ({
            id: item.creditCard.id,
            name: item.creditCard.name,
            number: item.creditCard.number,
            type: item.creditCard.type,
            currency: null,
            vendor: null,
          } as RequestableCreditCardOption)
        : null,
      amount: String(item.amount),
      subtotal: String(item.amount),
      taxMode: item.taxMode,
      taxValue: item.taxValue,
      taxAmount: '0',
      taxId: item.taxId,
      taxName: item.taxName,
      taxPercentage: item.taxPercentage,
      taxIsInclusive: false,
      transactionDate: item.transactionDate,
      attachment: item.attachment,
      notes: item.notes,
      expenseTypeData: item.expenseTypeData,
    })),
  }
}

export const useExpenseFormInitialization = () => {
  const _baseCurrency = useSelector(makeSelectBaseCurrency())
  const baseCurrency = new Currency({
    ..._baseCurrency,
    rate: Number.parseFloat(_baseCurrency.rate),
  })

  const { data: formData, isLoading: isFormDataLoading } = useQuery({
    queryKey: expenseQueryConfig.requestFormData().queryKey,
    queryFn: () => getExpenseRequestFormDataUseCase.execute({ baseCurrency }),
  })

  const [draftId, setDraftId] = useState<Nullable<number>>(null)
  const [formKey, setFormKey] = useState(0)

  useEffect(() => {
    if (formData?.draft) {
      setDraftId(formData.draft.id)
    }
  }, [formData])

  const initialValues = useMemo(
    (): ExpenseReportFormSchema =>
      formData ? mapFormInitialDataToSchema(formData) : DEFAULT_FORM_VALUES,
    [formData]
  )

  const isDraft = draftId !== null

  return {
    formData,
    initialValues,
    isDraft,
    draftId,
    formKey,
    baseCurrency,
    isFormDataLoading,
    setDraftId,
    incrementFormKey: () => setFormKey((prev) => prev + 1),
  }
}
